import { Anchor, Box, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import {
  IconDownloadOutline,
  IconExternal,
  IconFileOutline,
  IconPhotosOutline,
  IconVideoOutline,
} from '@zillow/constellation';
import { AttachmentGuidelines, hasMissingAttachmentContent, TooltipWrapper } from '@zg-rentals/satellite-components';
import { format } from 'date-fns';
import { useContext, useEffect, useState } from 'react';
import { useRenterProfile_GetAttachmentsByConversationIdQuery } from '../../../../types/GraphqlTypes';
import { ModalType } from '../../../../types/ModalType';
import {
  ATTACHMENT_SIDEBAR_DOWNLOAD_CLICK_EVENT_TYPE_ID,
  ATTACHMENT_SIDEBAR_PREVIEW_CLICK_EVENT_TYPE_ID,
  emitAttachmentSidebarDownloadClickEvent,
  emitAttachmentSidebarPreviewClickEvent,
} from '../../../../constants/clickstreamEvents/actionSidebar';
import StringConstants from '../../../../constants/StringConstants';
import useModal from '../../../../hooks/useModal';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import conversionUtils from '../../../../utils/conversionUtils';
import InboxSidebarSection from '../InboxSidebarSection';
import { logAttachmentsRenderError } from '../../../MessageAttachments';
import {
  StyledActionIndicatorWrapper,
  StyledContentWrapper,
  StyledHorizontalCard,
  StyledIconWrapper,
  StyledImage,
} from './StyledComponents';
import { logApolloError } from '../../../../utils/errorUtils';
import { truncatedTextStyles } from '../../../../styles/text';
import { useAuth } from '@zg-rentals/auth-comet';

type GetAriaLabelArgs = {
  fileName?: string | null | undefined;
  fileSizeFormatted: string;
  isAttachmentExpired?: boolean | null | undefined;
  previewUrl?: string | null | undefined;
};

const LOGGER_NAME = 'RenterProfile_Attachments';
const { IMAGE_FILE_TYPE, IMPERSONATION_DETECTED, VIDEO_FILE_TYPE } = StringConstants.ATTACHMENTS;
const MAX_DISPLAYED_ATTACHMENTS = 3;

const Attachments = () => {
  const { clearModal } = useModal();
  let shouldLogAttachmentsRenderError = false;

  const { conversationId = '' } = useContext(InboxPageContext);
  const { user } = useAuth();
  const { data: attachmentsData, error: attachmentsError } = useRenterProfile_GetAttachmentsByConversationIdQuery({
    variables: { conversationId: conversationId! },
    fetchPolicy: 'cache-first',
    ssr: false,
  });
  const { activeModal, setActiveModal } = useModal();
  const [displayAll, setDisplayAll] = useState(false);

  useEffect(() => {
    if (shouldLogAttachmentsRenderError) {
      logAttachmentsRenderError({ conversationId, loggerName: LOGGER_NAME });
    }
  }, [shouldLogAttachmentsRenderError]);

  if (attachmentsError) {
    logApolloError(attachmentsError, {
      details: `${LOGGER_NAME} - Error fetching attachments`,
      conversationId,
    });
  }
  if (!attachmentsData || attachmentsData.ZRM_getAttachmentsByConversationId.attachments.length === 0) {
    return null;
  }

  const { attachments } = attachmentsData.ZRM_getAttachmentsByConversationId;
  const filteredAttachments = attachments.filter((attachment) => !attachment.isAttachmentDeleted);
  const attachmentsForDisplay = displayAll
    ? filteredAttachments
    : filteredAttachments.slice(0, MAX_DISPLAYED_ATTACHMENTS);

  const handleShowAttachmentModal = (): void => {
    setActiveModal(ModalType.AttachmentModal);
  };

  const handleEventTracking = (isImageFileType = false) => {
    if (isImageFileType) {
      emitAttachmentSidebarPreviewClickEvent(user);
    }

    emitAttachmentSidebarDownloadClickEvent(user);
  };

  const getAriaLabel = ({ fileName, fileSizeFormatted, isAttachmentExpired, previewUrl }: GetAriaLabelArgs) => {
    if (isAttachmentExpired) return `${fileName} has expired and can't be viewed.`;
    if (previewUrl) return `Open ${fileName} in a new tab`;

    return `Download ${fileName} ${fileSizeFormatted}`;
  };

  return (
    <>
      {activeModal === ModalType.AttachmentModal && <AttachmentGuidelines onClose={clearModal} />}
      <InboxSidebarSection title="Attachments">
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          <Paragraph textStyle="body-sm" css={{ marginBottom: 'tightest' }}>
            Attachments will be deleted 30 days after sending or receiving them on the platform.&nbsp;
            <Anchor asChild>
              <TextButton textStyle="body-sm-bold" onClick={handleShowAttachmentModal}>
                Read guidelines
              </TextButton>
            </Anchor>
          </Paragraph>

          {attachmentsForDisplay.map((attachment, index) => {
            const {
              attachmentAddedMs,
              attachmentMetadataStatus,
              downloadUrl,
              fileName = '',
              fileType = '',
              fileSizeBytes,
              isAttachmentExpired,
              previewUrl,
              zDocId,
            } = attachment;

            if ((fileName as string) && fileName?.includes('unknown')) return null;

            const hasMissingContent = hasMissingAttachmentContent(attachmentMetadataStatus, isAttachmentExpired);
            const fileSizeFormatted = fileSizeBytes ? conversionUtils.formatBytes(fileSizeBytes) : '';
            const ariaLabel = fileName
              ? getAriaLabel({
                  fileName,
                  fileSizeFormatted,
                  isAttachmentExpired,
                  previewUrl,
                })
              : `We're unable to display this file right now`;
            const dateAdded = attachmentAddedMs ? format(new Date(Number(attachmentAddedMs)), 'MMM dd') : '';
            const href = downloadUrl || previewUrl;
            const isImageFileType = (fileType as string) ? fileType?.includes(IMAGE_FILE_TYPE) : false;
            const isVideoFileType = (fileType as string) ? fileType?.includes(VIDEO_FILE_TYPE) : false;
            const isImpersonator = downloadUrl === IMPERSONATION_DETECTED || previewUrl === IMPERSONATION_DETECTED;
            const isDisabled = isAttachmentExpired || isImpersonator || hasMissingContent || href === '';
            const key = zDocId ? `renterProfileAttachment-${zDocId}` : `renterProfileAttachment-${index}`;
            const isZDocPreviewUrl = previewUrl?.includes('zillowdocs') && previewUrl?.includes('/image/preview');
            // zDocs API is capable of resizing images with height and width params
            // as opposed to images generated on the FE during upload
            const imageSrc = isZDocPreviewUrl ? `${previewUrl}&height=40&width=40` : previewUrl;

            if (hasMissingContent) shouldLogAttachmentsRenderError = true;

            return (
              <TooltipWrapper
                data-testid="attachments-card-list"
                key={key}
                tooltipPlacement="top-start"
                wrapInTooltip={isImpersonator}
              >
                <StyledHorizontalCard
                  data-eventid={
                    isImageFileType
                      ? ATTACHMENT_SIDEBAR_PREVIEW_CLICK_EVENT_TYPE_ID
                      : ATTACHMENT_SIDEBAR_DOWNLOAD_CLICK_EVENT_TYPE_ID
                  }
                  aria-label={ariaLabel}
                  aria-disabled={isDisabled}
                  disabled={isDisabled}
                  as={isDisabled ? 'button' : 'a'}
                  download={!isImageFileType ? fileName : undefined}
                  href={isDisabled ? undefined : href}
                  target={previewUrl ? '_blank' : undefined}
                  onClick={() => handleEventTracking(isImageFileType)}
                  $backgroundColor={isAttachmentExpired && 'gray100'}
                  id={zDocId}
                >
                  <StyledIconWrapper $color={isDisabled ? 'gray400' : 'gray500'}>
                    {isImageFileType && (
                      <>
                        {isDisabled ? (
                          <IconPhotosOutline alt="Placeholder image" />
                        ) : (
                          !!imageSrc && (
                            <StyledImage
                              src={imageSrc}
                              alt="Image attachment thumbnail in renter profile"
                              width={40}
                              height={40}
                            />
                          )
                        )}
                      </>
                    )}

                    {isVideoFileType && <IconVideoOutline />}

                    {!isImageFileType && !isVideoFileType && <IconFileOutline />}
                  </StyledIconWrapper>

                  {isAttachmentExpired ? (
                    <StyledContentWrapper data-testid="attachments-expired-text">
                      <Paragraph
                        textStyle="body-xs"
                        css={{ ...truncatedTextStyles, maxWidth: '100%', color: 'text.subtle' }}
                      >
                        {fileName}&nbsp;
                      </Paragraph>
                      <Text textStyle="body-xs" css={{ color: 'text.subtle' }}>
                        has expired and can't be viewed.
                      </Text>
                    </StyledContentWrapper>
                  ) : (
                    <>
                      {fileName ? (
                        <StyledContentWrapper>
                          <Paragraph
                            data-testid="attachments-file-name"
                            textStyle="body-sm"
                            css={{ ...truncatedTextStyles, maxWidth: '100%' }}
                          >
                            {fileName}
                          </Paragraph>
                          <Paragraph
                            data-testid="attachments-file-details"
                            textStyle="body-xs"
                            css={{ color: 'text.subtle' }}
                          >
                            {fileSizeFormatted}, Added {dateAdded}
                          </Paragraph>
                        </StyledContentWrapper>
                      ) : (
                        <Paragraph textStyle="body-xs" css={{ paddingLeft: 'tighter' }}>
                          We're unable to display this file right now
                        </Paragraph>
                      )}

                      <StyledActionIndicatorWrapper $color={isDisabled ? 'gray400' : 'brand'}>
                        {isImageFileType ? <IconExternal /> : <IconDownloadOutline />}
                      </StyledActionIndicatorWrapper>
                    </>
                  )}
                </StyledHorizontalCard>
              </TooltipWrapper>
            );
          })}

          {filteredAttachments.length > 3 && (
            <TextButton
              data-testid="attachments-toggle-btn"
              onClick={() => setDisplayAll(!displayAll)}
              textStyle="body-sm-bold"
              css={{ justifyContent: 'left', marginTop: 'tight' }}
            >
              {displayAll ? 'Show less' : 'Show all attachments'}
            </TextButton>
          )}
        </Box>
      </InboxSidebarSection>
    </>
  );
};

export default Attachments;
